import { useCallback, useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column } from 'react-table';

import { Action, ResourceType } from '../../../../../../typings/permissions';
import { useDialogContext } from '../../../../../contexts/DialogContext';
import { window } from '../../../../../globals';
import { useHasPermissionForTeamMembers } from '../../../../../helpers/permissions';
import useRemoveUsersFromTeam from '../../../../../hooks/api/teams/useRemoveUsersFromTeam';
import usePaginatedTeamMembers from '../../../../../hooks/api/teams/useTeamMembers';
import useHasPermission from '../../../../../shared/permissions/useHasPermission';
import { Pill } from '../../../../generic';
import { icons } from '../../../../generic/Icon';
import { DEFAULT_PAGE_SIZE } from '../../../../generic/Pagination/consts';
import SimpleReactTableWithPagination from '../../../../generic/SimpleReactTable/SimpleReactTableWithPagination';
import TableHeader from '../../../../generic/TableHeader';
import SettingsTableRowActions from '../../../shared/SettingsTableRowActions';
import type { GetTeamMembersResponse, GetTeamResponse } from '../../../../../../shared/types/routes/teams';
import type { TeamUser } from '../../../../../types';

import AddMembersModal from './AddMembersModal';
import TeamMembersTableRoleDropdown from './TeamMembersTableRoleDropdown';

import styles from './TeamMembersTable.module.less';

const defaultSortMode = [{ id: 'username', desc: false }];

const getCurrentUserId = () => window.user?.id;

type Props = {
  team: GetTeamResponse;
  search: string;
  isShowingAddMembersModal: boolean;
  isTeamRoleIDPManaged: boolean;
  isTeamMembershipIDPManaged: boolean;
  onHideAddMembersModal: () => void;
};

/**
 * Renders a paginated table of team members.
 */
const TeamMembersTable: VoidFunctionComponent<Props> = ({
  team,
  search,
  isShowingAddMembersModal,
  onHideAddMembersModal,
  isTeamRoleIDPManaged,
  isTeamMembershipIDPManaged,
}) => {
  const teamId = team.id;

  const { onConfirm } = useDialogContext();

  //  Permissions
  const hasPermission = useHasPermission();
  const hasPermissionForTeamMembers = useHasPermissionForTeamMembers();
  const canManageRoles = hasPermission({ resourceType: ResourceType.Role, action: Action.Manage });
  const canEditTeamUsers = hasPermissionForTeamMembers(Action.Edit, teamId);
  //  There is no Team.Delete permission, so we check edit.
  const getCanDeleteUser = useCallback(
    (userId: number) => {
      if (userId === getCurrentUserId() && !canManageRoles) {
        return false;
      }
      return canEditTeamUsers;
    },
    [canManageRoles, canEditTeamUsers],
  );

  const { mutate: removeUsersFromTeam } = useRemoveUsersFromTeam();
  const onRemoveClick = useCallback(
    (user: GetTeamMembersResponse['results'][number]) => {
      onConfirm({
        title: `Are you sure you want to remove "${user.username || user.email || 'user'}"?`,
        body: `Removed users will no longer be able to view this team's projects.`,
        onConfirm: () => removeUsersFromTeam({ teamId, users: [{ id: user.userId }] }),
        confirmText: 'Remove',
        confirmBtnType: 'error',
      });
    },
    [onConfirm, removeUsersFromTeam, teamId],
  );

  const { data, isLoading, page, pageSize, setPage, setPageSize } = usePaginatedTeamMembers(teamId, { search });

  const columns: Array<Column<TeamUser>> = useMemo(
    () => [
      {
        id: 'username',
        Header: <TableHeader title="Name" />,
        accessor: 'username',
        resizable: false,
        sortable: false,
        width: 350,
        style: {
          textOverflow: 'ellipsis',
        },
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine because we are in a useMemo
        Cell: ({ original }) => (
          <span className={styles.username}>
            {original.username} {original.isServiceAccount && <Pill color="blue">Service Account</Pill>}
          </span>
        ),
      },
      {
        id: 'email',
        Header: <TableHeader title="Email" />,
        accessor: 'email',
        resizable: false,
        sortable: false,
        width: 350,
        style: {
          textOverflow: 'ellipsis',
        },
      },
      {
        id: 'role',
        Header: <TableHeader title="Role" />,
        accessor: 'roleId',
        resizable: false,
        sortable: false,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine because we are in a useMemo
        Cell: ({ original }) => (
          <TeamMembersTableRoleDropdown
            userId={original.userId}
            teamId={teamId}
            roleId={original.roleId}
            isDisabled={isTeamRoleIDPManaged}
          />
        ),
        style: { overflow: 'visible' },
      },
      {
        id: 'actions',
        Header: <TableHeader title="Actions" />,
        resizable: false,
        sortable: false,
        width: 70,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine because we are in a useMemo
        Cell: ({ original }) => (
          <SettingsTableRowActions>
            <SettingsTableRowActions.IconButton
              tooltipId={`remove-team-member-btn-${original.userId}`}
              tooltip={
                !getCanDeleteUser(original.userId)
                  ? 'You are not permitted to remove this user from this team.'
                  : 'Remove Member'
              }
              icon={icons.trashRegular}
              isDisabled={!getCanDeleteUser(original.userId) || isTeamMembershipIDPManaged}
              onClick={() => onRemoveClick(original)}
            />
          </SettingsTableRowActions>
        ),
      },
    ],
    [teamId, getCanDeleteUser, onRemoveClick, isTeamRoleIDPManaged, isTeamMembershipIDPManaged],
  );

  return (
    <>
      <SimpleReactTableWithPagination
        data={data?.results ?? []}
        columns={columns}
        defaultSortMode={defaultSortMode}
        isLoading={isLoading}
        showPagination={(data?.results?.length ?? 0) > 20}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        defaultPageSize={DEFAULT_PAGE_SIZE}
        onPageSizeChange={setPageSize}
        onPageChange={setPage}
        totalPages={Math.ceil((data?.totalCount ?? 0) / pageSize)}
      />
      {isShowingAddMembersModal && (
        <AddMembersModal teamId={team.id} teamName={team.name} onHide={onHideAddMembersModal} />
      )}
    </>
  );
};

export default TeamMembersTable;
